import { FC, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Book, Download, Lock, Eye, Trash, Edit, MoreVertical } from 'lucide-react';
import { type DigitalMaterial } from '@/types/library';
import { useProfile } from '@/hooks/useProfile';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { useDigitalMaterials } from '@/hooks/useDigitalMaterials';
import { useToast } from '@/components/ui/use-toast';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface MaterialCardProps {
  material: DigitalMaterial;
  showAdminControls?: boolean;
  onDelete?: (material: DigitalMaterial) => void;
}

export const MaterialCard: FC<MaterialCardProps> = ({ 
  material, 
  showAdminControls = false,
  onDelete 
}) => {
  const { userProfile } = useProfile();
  const navigate = useNavigate();
  const { toast } = useToast();

  const canAccess = !material.isPremium || (userProfile?.isPremium && userProfile?.isActive);

  const handleRead = () => {
    if (material.type === 'PDF' && material.fileUrl) {
      window.open(material.fileUrl, '_blank');
    } else {
      navigate(`/library/article/${material.id}`);
    }
  };

  const handleDownload = async () => {
    if (material.fileUrl) {
      window.open(material.fileUrl, '_blank');
    }
  };

  const handleEdit = () => {
    navigate(`/admin/library/edit/${material.id}`);
  };

  return (
    <Card className={cn(
      "overflow-hidden transition-all duration-300 hover:shadow-lg w-full h-full flex flex-col",
      !canAccess && "opacity-75"
    )}>
      <div className="relative aspect-[3/2] overflow-hidden">
        <img
          src={material.coverImage}
          alt={material.title}
          className="object-cover w-full h-full transition-transform duration-300 hover:scale-105"
        />
        {material.isPremium && (
          <Badge className="absolute top-2 right-2 bg-gradient-to-r from-yellow-400 to-yellow-600">
            Premium
          </Badge>
        )}
        {showAdminControls && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-2 right-2 bg-white/90 hover:bg-white"
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleEdit}>
                <Edit className="h-4 w-4 mr-2" />
                Editar
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => onDelete?.(material)}
                className="text-destructive"
              >
                <Trash className="h-4 w-4 mr-2" />
                Eliminar
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <div className="p-4 flex flex-col flex-1">
        <div className="flex-1">
          <h3 className="font-semibold text-lg mb-2 line-clamp-2">
            {material.title}
          </h3>
          <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
            {material.description}
          </p>

          <div className="flex flex-wrap gap-2 mb-4">
            <Badge variant="outline" className="flex items-center gap-1">
              <Book className="w-3 h-3" />
              {material.type}
            </Badge>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 mt-auto">
          {material.type === 'PDF' ? (
            <Button
              onClick={handleDownload}
              className="flex-1"
              disabled={!canAccess}
            >
              {canAccess ? (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Descargar PDF
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4 mr-2" />
                  Premium
                </>
              )}
            </Button>
          ) : (
            <Button
              onClick={handleRead}
              className="flex-1"
              disabled={!canAccess}
            >
              {canAccess ? (
                <>
                  <Eye className="w-4 h-4 mr-2" />
                  Leer
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4 mr-2" />
                  Premium
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
};
